import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { FormField } from "@/components/core/form-field";
import { LoadingSkeleton } from "@/components/core/loading-skeleton";
import { Link } from "@/components/core/tanstack-link";
import { NotFound } from "@/components/not-found";
import { formatCurrency, formatDate } from "@/helpers/intl";
import { InvoiceStatusChip } from "@/helpers/labels";
import { InvoiceStatus, usePortal, usePortalInvoices } from "@/models/backend";

export const Route = createFileRoute("/_portal/my-invoices/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { token } = Route.useSearch();
  const { data: customer, isError, error } = usePortal({ token });
  const { data: container, isLoading } = usePortalInvoices({ token });
  const { invoices, plans } = container ?? { invoices: [], plans: [] };
  if (isError && "status" in error && error.status === 404) {
    return <NotFound />;
  }
  return (
    <Stack spacing={4}>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Typography variant="h2">{customer?.organization.name}</Typography>
        {isLoading ? (
          <LoadingSkeleton width="100px" sx={{ ml: "auto" }} />
        ) : invoices?.find((inv) => inv.status === InvoiceStatus.overdue) ? (
          <Chip sx={{ ml: "auto" }} color="warning" variant="outlined" label="Invoices overdue" />
        ) : invoices?.find((inv) => inv.status === InvoiceStatus.posted) ? (
          <Chip
            sx={{ ml: "auto" }}
            color="secondary"
            variant="outlined"
            label="Invoices outstanding"
          />
        ) : (
          <Chip
            sx={{ ml: "auto" }}
            color="primary"
            variant="outlined"
            label="Payments up to date"
          />
        )}
      </Stack>
      <Stack spacing={2}>
        <Typography variant="h4">Billing information</Typography>
        <Stack sx={{ pl: "16px" }}>
          <FormField label="Billed to" component={Typography}>
            {customer?.name}
          </FormField>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        <Typography variant="h4">Outstanding invoices</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice no.</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Amount due</TableCell>
              <TableCell>Amount paid</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices?.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>
                  <Link
                    to="/my-invoices/$invoiceId"
                    params={{ invoiceId: invoice.id }}
                    search={{ token }}
                  >
                    {invoice.number}
                  </Link>
                </TableCell>
                <TableCell>{formatDate(invoice.due)}</TableCell>
                <TableCell>{formatCurrency(invoice.amount_due, invoice.currency)}</TableCell>
                <TableCell>{formatCurrency(invoice.amount_paid, invoice.currency)}</TableCell>
                <TableCell>
                  <InvoiceStatusChip invoice={invoice} />
                </TableCell>
              </TableRow>
            ))}
            {plans.map((plan) => (
              <>
                <TableRow key={plan.id}>
                  <TableCell colSpan={5}>
                    Payment plan for{" "}
                    {plan.invoices.map((inv, idx) => (
                      <span key={inv.id}>
                        {idx > 0 && ", "}
                        <Link
                          to="/my-invoices/$invoiceId"
                          search={{ token }}
                          params={{ invoiceId: inv.id }}
                        >
                          {inv.number}
                        </Link>
                      </span>
                    ))}
                  </TableCell>
                </TableRow>
                {plan.installments.map((installment) => (
                  <TableRow key={installment.id}>
                    <TableCell sx={{ pl: 6 }}>
                      <Link
                        to="/my-invoices/pp/$installmentId"
                        search={{ token }}
                        params={{ installmentId: installment.id }}
                      >
                        {installment.number}
                      </Link>
                    </TableCell>
                    <TableCell>{formatDate(installment.due)}</TableCell>
                    <TableCell>
                      {formatCurrency(installment.amount_due, installment.currency)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(installment.amount_paid, installment.currency)}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusChip invoice={installment} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Stack>
  );
}
